/**
 * API Endpoint Definitions
 *
 * Configurable URL builders for all API endpoints.
 * Each app can create endpoints with its own base URL.
 *
 * Usage:
 * - Same-origin (old frontend, admin): createEndpoints()
 * - Cross-origin (extension, company): createEndpoints('https://api.automadoc.cc')
 */

/**
 * Create API endpoint builders with configurable base URL
 * @param baseUrl - API base URL (default: '' for same-origin)
 */
export function createEndpoints(baseUrl = '') {
	return {
		// =========================================================================
		// Templates - baseToken and tableId via headers
		// =========================================================================
		getTemplates: (): string =>
			`${baseUrl}/api/templates`,

		createTemplate: (): string =>
			`${baseUrl}/api/templates`,

		updateTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}`,

		claimTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/claim`,

		deleteTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}`,

		uploadTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/upload`,

		autoGenerateTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/auto-generate`,

		downloadTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/download`,

		/** GET: the template as an `.automadoc` file. */
		exportTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/export`,

		/** POST (multipart `file`): an `.automadoc` file into the table in the headers. */
		importTemplate: (): string =>
			`${baseUrl}/api/templates/import`,

		/** POST: a copy here, or in another table or Base (DuplicateTemplateRequest). */
		duplicateTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/duplicate`,

		/** GET: the company's Bases and tables a template can be copied to. */
		templateCopyTargets: (): string =>
			`${baseUrl}/api/templates/copy-targets`,

		// =========================================================================
		// Document API (called by the customer's own systems with an API key)
		// =========================================================================
		createDocuments: (templateId: string): string =>
			`${baseUrl}/api/v1/templates/${templateId}/documents`,

		documentStatus: (generationId: string): string =>
			`${baseUrl}/api/v1/documents/${generationId}`,

		/** The finished file itself, with the same key (for callers that cannot follow a link). */
		documentFile: (generationId: string): string =>
			`${baseUrl}/api/v1/documents/${generationId}/file`,

		/** The templates a company key opens. */
		apiTemplates: (): string =>
			`${baseUrl}/api/v1/templates`,

		/** One template and the schema of its `data`. */
		apiTemplate: (templateId: string): string =>
			`${baseUrl}/api/v1/templates/${templateId}`,

		/** Template designer: GET the design, PUT to save the draft. */
		templateDesign: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/design`,
		templateDesignPublish: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/design/publish`,
		/** PUT the Base table's fields for the designer (the extension, which can read them). */
		templateDesignFields: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/design/fields`,

		generate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/generate`,

		downloadGeneration: (generationId: string): string =>
			`${baseUrl}/api/generations/${generationId}/download`,

		// =========================================================================
		// Tenant endpoints (web app routes)
		// =========================================================================
		getTenantInfo: (): string => `${baseUrl}/api/tenant`,

		getTenantTemplates: (): string => `${baseUrl}/api/tenant/templates`,

		/** Every template of the account, whatever feeds it (the portal's Templates table). */
		tenantTemplateRows: (): string => `${baseUrl}/api/tenant/templates/all`,

		/** Everyone in the account, with role and last activity (the portal's People table). */
		tenantPeople: (): string => `${baseUrl}/api/tenant/people`,

		getTenantGenerations: (): string => `${baseUrl}/api/tenant/generations`,

		deleteTenantTemplate: (templateId: string): string =>
			`${baseUrl}/api/tenant/templates/${templateId}`,

		getTenantUsers: (): string => `${baseUrl}/api/tenant/users`,

		// Approval templates (app_admin). Deleting one is deleteTenantTemplate.
		/** GET to list, POST to connect an approval definition. */
		approvalTemplates: (): string => `${baseUrl}/api/tenant/approvals`,
		/** GET the template with its variables, PUT to update it. */
		approvalTemplate: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}`,
		approvalTemplateUpload: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/upload`,
		approvalTemplateStarter: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/starter`,
		approvalTemplateDownload: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/download`,
		approvalTemplateExport: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/export`,
		/** POST (multipart `file`, `link`, `locale`, `timezone`). Not under /approvals/: it would read as a template id. */
		approvalTemplateImport: (): string => `${baseUrl}/api/tenant/approval-import`,
		/** GET to list, POST `{ name }` to create — tenant-wide API keys (app_admin). */
		tenantApiKeys: (): string => `${baseUrl}/api/tenant/api-keys`,
		/**
		 * A non-Lark account's source and `api` templates (the Word add-in's routes, which the
		 * portal's Templates page uses too): GET to list, POST `{ name, sourceType: 'api' }`.
		 */
		authoringTemplates: (): string => `${baseUrl}/api/authoring/templates`,
		/** POST a multipart `file` to store it as the template's file. */
		authoringTemplateUpload: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/upload`,
		authoringTemplateDownload: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/download`,
		/** GET one account template's settings (AuthoringTemplateDetail), PUT to change them. */
		authoringTemplate: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}`,
		/** GET: the columns its key, status and save settings can name (SourceColumnsResponse). */
		authoringTemplateColumns: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/columns`,
		/** POST: a starter template from its fields (or sample data), stored as its file. */
		authoringTemplateStarter: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/starter`,
		/**
		 * The Company Portal's Generate page: GET what it needs (PortalGenerateInfo), POST `{ data }`
		 * to make documents from it as the person signed in (PortalGenerateResponse).
		 */
		authoringTemplateGenerate: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/generate`,
		/** PATCH (with X-Source-Key): the caller becomes the owner of a source template, for runs nobody attends. */
		sourceTemplateClaim: (templateId: string): string => `${baseUrl}/api/source/templates/${encodeURIComponent(templateId)}/claim`,
		/** POST `{ name, view }`: a new source template (SharePoint list, Dataverse table) bound with the caller's own connection. */
		sourceTemplates: (): string => `${baseUrl}/api/source/templates`,
		/** GET: SharePoint sites the caller can open (`q` searches). */
		authoringSharePointSites: (q: string): string => `${baseUrl}/api/authoring/pickers/sharepoint/sites?q=${encodeURIComponent(q)}`,
		authoringSharePointLists: (siteId: string): string => `${baseUrl}/api/authoring/pickers/sharepoint/sites/${encodeURIComponent(siteId)}/lists`,
		/** GET: the tables of a Dataverse environment the caller can see. */
		authoringDataverseTables: (environmentUrl: string): string => `${baseUrl}/api/authoring/pickers/dataverse/tables?environmentUrl=${encodeURIComponent(environmentUrl)}`,
		/** Template designer for an account template: GET the design, PUT to save the draft. */
		authoringTemplateDesign: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/design`,
		authoringTemplateDesignPublish: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/design/publish`,
		/** PUT `{ sample }`: sample data (an `api` template's fields come from it). */
		authoringTemplateDesignSample: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/design/sample`,
		/** POST `{ sample? }`: one test document, from the sample or the source's first record. */
		authoringTemplateDesignPreview: (templateId: string): string => `${baseUrl}/api/authoring/templates/${encodeURIComponent(templateId)}/design/preview`,
		/** DELETE to revoke. */
		tenantApiKey: (keyId: string): string => `${baseUrl}/api/tenant/api-keys/${encodeURIComponent(keyId)}`,
		/** GET AutomaDoc-managed members (app_admin). */
		tenantMembers: (): string => `${baseUrl}/api/tenant/members`,
		/** PUT `{ role }` to promote, demote or restore, DELETE to remove. */
		tenantMember: (userId: string): string => `${baseUrl}/api/tenant/members/${encodeURIComponent(userId)}`,
		/** GET `{ policy, applies, organisation }`, PUT `{ policy: 'open' | 'approval' }` (app_admin, fresh). */
		tenantJoinPolicy: (): string => `${baseUrl}/api/tenant/join-policy`,
		/** GET the people waiting for approval (app_admin). */
		tenantJoinRequests: (): string => `${baseUrl}/api/tenant/join-requests`,
		/** POST `{ role? }` to approve (fresh admin). */
		tenantJoinRequestApprove: (userId: string): string => `${baseUrl}/api/tenant/join-requests/${encodeURIComponent(userId)}/approve`,
		/** POST to decline (fresh admin): they stay out until approved or restored. */
		tenantJoinRequestDecline: (userId: string): string => `${baseUrl}/api/tenant/join-requests/${encodeURIComponent(userId)}/decline`,
		/** GET pending invitations, POST `{ email, role?, seat?, locale? }` to invite (every account). */
		tenantInvitations: (): string => `${baseUrl}/api/tenant/invitations`,
		/** DELETE to withdraw an invitation. */
		tenantInvitation: (invitationId: string): string => `${baseUrl}/api/tenant/invitations/${encodeURIComponent(invitationId)}`,
		/** POST `{ locale? }`: a new link (the old one stops working) and the email again. */
		tenantInvitationResend: (invitationId: string): string => `${baseUrl}/api/tenant/invitations/${encodeURIComponent(invitationId)}/resend`,
		/** GET the caller's connection, POST to plan connecting a purpose, DELETE to disconnect (any account); `…/complete` beside it. */
		tenantConnection: (provider: 'microsoft' | 'google'): string => `${baseUrl}/api/tenant/connections/${provider}`,
		/** GET the Dataverse environments the caller can reach (slice B). */
		dataverseEnvironments: (): string => `${baseUrl}/api/tenant/connections/microsoft/dataverse/environments`,
		approvalRequests: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/requests`,
		/** GET the recent runs, POST to start a test or a regeneration. */
		approvalRuns: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/runs`,
		/** Not under /approvals/: a sibling there would read as a template id. */
		approvalChats: (): string => `${baseUrl}/api/tenant/approval-chats`,
		/** Template designer: GET the design, PUT to save the draft. */
		approvalDesign: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/design`,
		approvalDesignPublish: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/design/publish`,
		/** POST: the owner confirms the current file fits the form as it is now. */
		approvalFormChecked: (templateId: string): string => `${baseUrl}/api/tenant/approvals/${templateId}/form-checked`,
		/** GET every signature image the company uploaded — they belong to it, not to a template. */
		approverSignatures: (): string => `${baseUrl}/api/tenant/approver-signatures`,
		/** PUT (multipart `file`) to upload or replace one person's image, DELETE to remove it. */
		approverSignature: (openId: string): string => `${baseUrl}/api/tenant/approver-signatures/${encodeURIComponent(openId)}`,

		// E-signature. The extension's routes are the sender's own requests; the
		// /tenant ones are the whole company, for app admins in the portal.
		signatures: (): string => `${baseUrl}/api/signatures`,
		signatureColleagues: (): string => `${baseUrl}/api/signatures/colleagues`,
		signatureEligibility: (generationId: string): string => `${baseUrl}/api/signatures/generations/${generationId}`,
		signatureSettings: (templateId: string): string => `${baseUrl}/api/signatures/templates/${templateId}/settings`,
		signatureRequest: (requestId: string, scope: 'own' | 'tenant' = 'own'): string =>
			`${baseUrl}/api${scope === 'tenant' ? '/tenant' : ''}/signatures/${requestId}`,
		tenantSignatures: (): string => `${baseUrl}/api/tenant/signatures`,
		/** Public, for the signing page: the link token is the only credential. */
		signingLink: (token: string): string => `${baseUrl}/api/sign/${token}`,

		getTenantBases: (): string => `${baseUrl}/api/tenant/bases`,

		getLarkBases: (): string => `${baseUrl}/api/tenant/lark-bases`,

		refreshNames: (): string => `${baseUrl}/api/tenant/refresh-names`,

		startTrial: (): string => `${baseUrl}/api/tenant/start-trial`,

		billingPortal: (): string => `${baseUrl}/api/tenant/billing-portal`,

		billingExtraTemplates: (): string => `${baseUrl}/api/tenant/billing/extra-templates`,
		checkoutToken: (): string => `${baseUrl}/api/tenant/checkout-token`,
		/** Seats, packs, the automation allowance and what this environment sells. app_admin. */
		billingSummary: (): string => `${baseUrl}/api/tenant/billing/summary`,
		/** POST a seat subscription's new total quantity. app_admin. */
		billingSeats: (): string => `${baseUrl}/api/tenant/billing/seats`,
		/** GET who holds a seat, PUT to assign or unassign one. app_admin. */
		tenantSeats: (): string => `${baseUrl}/api/tenant/seats`,

		// =========================================================================
		// Auth endpoints (JWT-based)
		// =========================================================================
		authLogin: (): string => `${baseUrl}/api/auth/login`,

		authRefresh: (): string => `${baseUrl}/api/auth/refresh`,

		authStatus: (): string => `${baseUrl}/api/auth/status`,

		authLogout: (): string => `${baseUrl}/api/auth/logout`,

		authCheckInstallation: (): string => `${baseUrl}/api/auth/check-installation`,

		// =========================================================================
		// User endpoints
		// =========================================================================
		getMe: (): string => `${baseUrl}/api/me`,

		// =========================================================================
		// Support endpoints
		// =========================================================================
		supportContact: (): string => `${baseUrl}/api/support/contact`,

		supportRequest: (): string => `${baseUrl}/api/support/request`,

		// The signed-in person's own contact preferences (contact center)
		contactPreferences: (): string => `${baseUrl}/api/tenant/contact-preferences`,
	}
}

/**
 * Create Admin API endpoint builders
 * Admin endpoints are on admin.automadoc.cc (same-origin for admin app)
 * @param baseUrl - Admin API base URL (default: '' for same-origin)
 */
export function createAdminEndpoints(baseUrl = '') {
	// Tenant keys are encoded: an account beyond Lark is keyed `ms:<tid>`, `google:<hd>`
	// or `email:<ulid>`, and a Lark key encodes to itself. Hono decodes the param back.
	return {
		// =========================================================================
		// Tenant management
		// =========================================================================
		listTenants: (): string => `${baseUrl}/api/tenants`,

		getTenant: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}`,

		updateTenant: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}`,

		getTenantBases: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/bases`,

		getTenantUsers: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/users`,

		getTenantTables: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/tables`,

		getTenantGenerations: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/generations`,

		getTenantTemplates: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/templates`,

		/** The grant ledger: GET to list, POST to issue a manual grant. */
		getTenantGrants: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/grants`,

		/** DELETE to revoke. Paddle grants are refused — cancel those in Paddle. */
		revokeTenantGrant: (tenantKey: string, grantId: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/grants/${grantId}`,

		/** Document packs: GET to list (history included), POST to issue a manual or gift pack. */
		getTenantPacks: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/packs`,

		/** DELETE to reverse. A Paddle pack is refunded in Paddle instead. */
		reverseTenantPack: (tenantKey: string, packId: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/packs/${packId}`,

		/** Seat assignments and the seats in force per tier. */
		getTenantSeats: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/seats`,

		/** Identities, members, connections (never a token) and session counts. */
		getTenantIdentity: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/identity`,

		/** POST. Signs out every non-Lark session of the account. */
		revokeTenantSessions: (tenantKey: string): string =>
			`${baseUrl}/api/tenants/${encodeURIComponent(tenantKey)}/sessions/revoke`,

		downloadTemplate: (templateId: string): string =>
			`${baseUrl}/api/templates/${templateId}/download`,

		downloadGeneration: (generationId: string): string =>
			`${baseUrl}/api/generations/${generationId}/download`,

		// =========================================================================
		// CRON endpoints
		// =========================================================================
		triggerQuotaUpdate: (): string => `${baseUrl}/api/cron/update-quotas`,

		triggerCleanup: (): string => `${baseUrl}/api/cron/cleanup`,

		triggerRefreshTokens: (): string => `${baseUrl}/api/cron/refresh-tokens`,

		// =========================================================================
		// Worker endpoints
		// =========================================================================
		getWorkerStatus: (): string => `${baseUrl}/api/worker/status`,

		getJobDetails: (jobId: string): string =>
			`${baseUrl}/api/worker/jobs/${jobId}`,

		cancelJob: (jobId: string): string =>
			`${baseUrl}/api/worker/jobs/${jobId}/cancel`,

		// =========================================================================
		// Lark endpoints
		// =========================================================================
		getAppTicketStatus: (): string => `${baseUrl}/api/lark/app-ticket-status`,

		resendAppTicket: (): string => `${baseUrl}/api/lark/resend-app-ticket`,

		// =========================================================================
		// Contact Center
		// =========================================================================
		getConversationStats: (): string => `${baseUrl}/api/conversations/stats`,

		listConversations: (): string => `${baseUrl}/api/conversations`,

		initiateConversation: (): string => `${baseUrl}/api/conversations/initiate`,

		getConversation: (conversationId: string): string =>
			`${baseUrl}/api/conversations/${conversationId}`,

		replyConversation: (conversationId: string): string =>
			`${baseUrl}/api/conversations/${conversationId}/reply`,

		updateConversation: (conversationId: string): string =>
			`${baseUrl}/api/conversations/${conversationId}`,

		downloadMessageFile: (messageId: string): string =>
			`${baseUrl}/api/messages/${messageId}/file`,

		// Broadcasts (contact center): `action` is estimate, preview, test, send, unschedule,
		// cancel, rerun or deliveries.
		broadcasts: (): string => `${baseUrl}/api/broadcasts`,

		broadcast: (id: string): string => `${baseUrl}/api/broadcasts/${encodeURIComponent(id)}`,

		broadcastAction: (id: string, action: string): string =>
			`${baseUrl}/api/broadcasts/${encodeURIComponent(id)}/${action}`,

	}
}

// Pre-configured for same-origin usage
export const API_ENDPOINTS = createEndpoints()
export const ADMIN_ENDPOINTS = createAdminEndpoints()
